import logger from 'shared/services/logger'
import { cssValueToPx } from 'shared/utils/cssUnits'
import { getCdnBase } from 'shared/utils/imageUtils'
import { type SrcSetObject } from 'views/components/atoms/ResponsiveImage/helpers/types'
import { BREAKPOINT_MAP } from 'views/consts/imgBreakpoints'
import cssVars from 'views/utils/cssVars'

const FONT_SIZE_BASE_PX = cssValueToPx(cssVars.typography['font-size-base'])

const BREAKPOINT_VALUES_PX = Object.values(BREAKPOINT_MAP)
  .join()
  .match(/[0-9]+r?em/g)!
  .map(em => parseInt(em, 10) * FONT_SIZE_BASE_PX)

export const createRemoveCloseNeighboursReducer = (similarity: number) => {
  return (pxs: Array<number>, px: number): Array<number> => {
    const prev = pxs[pxs.length - 1]
    const ratio = px > prev ? prev / px : px / prev
    return ratio > similarity ? pxs : [...pxs, px]
  }
}

// Our CDN doesn’t really serve images bigger than this, so there’s no point asking
const MAX_IMAGE_SIZE = 1000

const DPIS = [1, 1.5, 2, 3]

const BASE_IMAGE_SIZES: Readonly<Array<number>> = [
  // Hard coded small values
  50,
  100,
  150,

  // For commonly used viewport fractions
  ...BREAKPOINT_VALUES_PX.map(px => px / 4), // 25vw
  ...BREAKPOINT_VALUES_PX.map(px => px / 3), // 33vw
  ...BREAKPOINT_VALUES_PX.map(px => px / 2), // 50vw
  ...BREAKPOINT_VALUES_PX, // 100vw

  // Hard coded big values
  MAX_IMAGE_SIZE,
]

export const normaliseFileName = (cdnUrl: string, fileName: string): string => {
  const cdnBase = getCdnBase(cdnUrl)
  let normalisedFileName: string

  if (fileName.startsWith(cdnBase)) {
    normalisedFileName = fileName
      .substring(cdnBase.length)
      .replace(/^[0-9]+x[0-9]+\//, '')
  } else {
    if (/^[a-z]+:\/\/|^\.\.?\//.test(fileName)) {
      throw new Error(`Non–CDN URL passed to CdnImage: ${fileName}`)
    }

    normalisedFileName = fileName
  }

  return normalisedFileName
}

const getImageSizes = (min: number, max: number): Array<number> => {
  if (min > max) {
    ;[min, max] = [max, min]
    logger.warn('CdnImage was given minWidth > maxWidth, swapping values')
  }

  let imageSizes: Array<number> = [...BASE_IMAGE_SIZES].filter(
    s => min <= s && s <= max
  )

  // We can end up with empty array, if min and max are between all of
  // our base sizes.
  if (imageSizes.length === 0) {
    imageSizes = [BASE_IMAGE_SIZES.find(s => s > max)!]
  }

  return (
    imageSizes
      // multiply for common DPIs
      .reduce(
        (pxs, px) => [...pxs, ...DPIS.map(dpi => dpi * px)],
        [] as Array<number>
      )
      // Drop sizes larger than what our CDN serves
      .filter(px => px <= MAX_IMAGE_SIZE)
      // round down
      .map(px => parseInt(px as unknown as string, 10))
      // sort
      .sort((a, b) => b - a)
      // filter values that are close to each other
      // Using .reduce, not .filter, because we want to compare with
      // previous element from already filtered array, not the source
      .reduce(createRemoveCloseNeighboursReducer(0.95), [])
  )
}

export const generateSrcSet = (
  cdnUrl: string,
  fileName: string,
  { minWidth, maxWidth }: { minWidth: number; maxWidth: number }
): SrcSetObject => {
  const cdnBase = getCdnBase(cdnUrl)
  const imageSizes = getImageSizes(minWidth, maxWidth)
  const srcSetEntries = imageSizes.map(size => [
    `${size}w`,
    `${cdnBase}${size}x0/${fileName}`,
  ])

  return Object.fromEntries(srcSetEntries)
}

export const generateWebPSrcSet = (srcSet: SrcSetObject): SrcSetObject => {
  return Object.fromEntries(
    Object.entries(srcSet).map(([sizeDesc, url]) => [
      sizeDesc,
      url.replace(/\.[^./]+$/, '.webp'),
    ])
  )
}
